import { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'tenant_admin', label: 'Administrador', color: '#DC2626', bg: '#FEE2E2', desc: 'Acesso total ao sistema, incluindo usuários e assinatura' },
  { value: 'manager',      label: 'Gerente',        color: '#D97706', bg: '#FEF3C7', desc: 'Gerencia equipe, serviços, O.S. e relatórios' },
  { value: 'attendant',    label: 'Atendente',      color: '#2563EB', bg: '#DBEAFE', desc: 'Agenda, clientes, veículos e O.S.' },
  { value: 'financial',    label: 'Financeiro',     color: '#059669', bg: '#D1FAE5', desc: 'Acesso ao módulo financeiro e relatórios' },
  { value: 'employee',     label: 'Funcionário',    color: '#7C3AED', bg: '#EDE9FE', desc: 'Visualiza e executa O.S. atribuídas' },
];

function roleMeta(value) {
  return ROLES.find(r => r.value === value) || { label: value, color: '#6B7280', bg: '#F3F4F6' };
}

const EMPTY_FORM = { name: '', email: '', password: '', role: 'attendant', phone: '' };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0F172A', margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
  fontSize: '.9rem', outline: 'none', boxSizing: 'border-box', color: '#0F172A',
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // 'create' | { user }
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [resetPw, setResetPw] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await usersAPI.list();
      setUsers(r.data.data || []);
    } catch { toast.error('Erro ao carregar usuários.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setModal('create'); };
  const openEdit   = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, phone: u.phone || '' });
    setResetPw('');
    setModal({ user: u });
  };
  const closeModal = () => setModal(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('Preencha nome, e-mail e senha.');
    setSaving(true);
    try {
      await usersAPI.create(form);
      toast.success('Usuário criado!');
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao criar usuário.');
    } finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Nome obrigatório.');
    setSaving(true);
    const payload = { name: form.name, role: form.role, phone: form.phone };
    if (resetPw) payload.password = resetPw;
    try {
      await usersAPI.update(modal.user.id, payload);
      toast.success('Usuário atualizado!');
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar.');
    } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await usersAPI.update(u.id, { is_active: u.is_active ? 0 : 1 });
      toast.success(u.is_active ? 'Usuário desativado.' : 'Usuário reativado.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro.');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Desativar "${u.name}"?`)) return;
    try {
      await usersAPI.delete(u.id);
      toast.success('Usuário desativado.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao desativar.');
    }
  };

  const isMe = (u) => u.id === me?.id;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', margin: 0 }}>Usuários</h1>
          <p style={{ color: '#6B7280', fontSize: '.85rem', marginTop: 4 }}>Gerencie quem acessa o sistema e seus níveis de permissão</p>
        </div>
        <button onClick={openCreate}
          style={{ padding: '10px 20px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '.9rem' }}>
          + Novo usuário
        </button>
      </div>

      {/* Role legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {ROLES.map(r => (
          <span key={r.value} style={{ background: r.bg, color: r.color, borderRadius: 6, padding: '3px 10px', fontSize: '.73rem', fontWeight: 700 }}>
            {r.label}
          </span>
        ))}
      </div>

      {/* Users table */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF' }}>Carregando...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.87rem' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Nome', 'E-mail', 'Perfil', 'Telefone', 'Status', 'Ações'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: '.75rem', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const rm = roleMeta(u.role);
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: u.is_active ? 1 : 0.5 }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600, color: '#0F172A' }}>
                        {u.name} {isMe(u) && <span style={{ fontSize: '.7rem', color: '#6B7280', fontWeight: 400 }}>(você)</span>}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#374151' }}>{u.email}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: rm.bg, color: rm.color, borderRadius: 6, padding: '2px 8px', fontSize: '.72rem', fontWeight: 700 }}>{rm.label}</span>
                      </td>
                      <td style={{ padding: '10px 16px', color: '#6B7280' }}>{u.phone || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: u.is_active ? '#D1FAE5' : '#F3F4F6', color: u.is_active ? '#059669' : '#9CA3AF', borderRadius: 6, padding: '2px 8px', fontSize: '.72rem', fontWeight: 700 }}>
                          {u.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => openEdit(u)}
                            style={{ padding: '4px 10px', background: '#F3F4F6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '.78rem', fontWeight: 600 }}>
                            Editar
                          </button>
                          {!isMe(u) && (
                            <button onClick={() => toggleActive(u)}
                              style={{ padding: '4px 10px', background: u.is_active ? '#FEF3C7' : '#D1FAE5', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '.78rem', fontWeight: 600, color: u.is_active ? '#92400E' : '#065F46' }}>
                              {u.is_active ? 'Desativar' : 'Ativar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                      Nenhum usuário cadastrado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      {modal === 'create' && (
        <Modal title="Novo usuário" onClose={closeModal}>
          <form onSubmit={handleCreate}>
            <Field label="Nome completo">
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome" />
            </Field>
            <Field label="E-mail">
              <input type="email" style={inputStyle} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" />
            </Field>
            <Field label="Senha">
              <input type="password" style={inputStyle} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
            </Field>
            <Field label="Telefone">
              <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(00) 00000-0000" />
            </Field>
            <Field label="Perfil de acesso">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ROLES.map(r => (
                  <label key={r.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${form.role === r.value ? r.color : '#E5E7EB'}`, background: form.role === r.value ? r.bg : '#fff' }}>
                    <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => setForm(f => ({ ...f, role: r.value }))} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: r.color, fontSize: '.85rem' }}>{r.label}</div>
                      <div style={{ fontSize: '.75rem', color: '#6B7280' }}>{r.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Field>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" onClick={closeModal}
                style={{ flex: 1, padding: '10px', background: '#F3F4F6', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                style={{ flex: 2, padding: '10px', background: saving ? '#93C5FD' : '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                {saving ? 'Salvando...' : 'Criar usuário'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {modal?.user && (
        <Modal title={`Editar — ${modal.user.name}`} onClose={closeModal}>
          <form onSubmit={handleEdit}>
            <Field label="Nome completo">
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Telefone">
              <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(00) 00000-0000" />
            </Field>
            {!isMe(modal.user) && (
              <Field label="Perfil de acesso">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ROLES.map(r => (
                    <label key={r.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${form.role === r.value ? r.color : '#E5E7EB'}`, background: form.role === r.value ? r.bg : '#fff' }}>
                      <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => setForm(f => ({ ...f, role: r.value }))} style={{ marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 700, color: r.color, fontSize: '.85rem' }}>{r.label}</div>
                        <div style={{ fontSize: '.75rem', color: '#6B7280' }}>{r.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <Field label="Redefinir senha (opcional)">
              <input type="password" style={inputStyle} value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Deixe em branco para manter a atual" />
            </Field>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" onClick={closeModal}
                style={{ flex: 1, padding: '10px', background: '#F3F4F6', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                style={{ flex: 2, padding: '10px', background: saving ? '#93C5FD' : '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                {saving ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
